// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
/* eslint-disable react/no-array-index-key */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import quickLinkCardStyles from "@werner/App/SharedStyle/MyAccount/QuickLinkCardStyles";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { QuickLinkCardComponent } from "@werner/App/Widgets/Baic/QuickLinkCard";
import { color, layoutContainer, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    wrapperPadding = "wrapperPadding",
    bottomPadding = "bottomPadding",
}

export interface QuickLinkModel {
    fields: {
        destination: LinkFieldValue;
        linkText: string;
        image: string;
        text: string;
        openInNewWindow: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: QuickLinkModel[];
        [fields.wrapperPadding]: string;
        [fields.bottomPadding]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
});

export interface QuickLinksStyles {
    wrapper?: InjectableCss;
    innerContainer?: InjectableCss;
    quickLinkWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    textWrapper?: InjectableCss;
    text?: TypographyProps;
    link?: LinkProps;
}

export const quickLinksStyles: QuickLinksStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;
            background-color: ${color("primary", 0.05)};
        `,
    },
    innerContainer: {
        css: css`
            ${layoutContainer}
            padding-top: 86px;
            padding-bottom: 86px;
            display: flex;
            justify-content: space-between;
            gap: 48px 28px;
            flex-wrap: wrap;
            flex: 1;
            ${xScreen("lte", "md")} {
                justify-content: center;
            }
        `,
    },
    textWrapper: {
        css: css`
            background: ${color("common.white")};
        `,
    },
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const QuickLinks: FC<Props> = ({ fields, isAuthenticated }) => {
    const { isEditing } = useShellContext();

    const links = useGetLinks(fields.links, o => o.fields.destination);
    const [styles] = React.useState(() => mergeWithCss(quickLinksStyles, quickLinkCardStyles));

    if (!isAuthenticated && !isEditing) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.innerContainer}>
                {links.map((link, index) => (
                    <React.Fragment key={`${link.url}_${index}`}>
                        <QuickLinkCardComponent
                            fields={{
                                destination: fields.links[index].fields.destination,
                                openInNewWindow: fields.links[index].fields.openInNewWindow,
                                linkText: fields.links[index].fields.linkText,
                                image: fields.links[index].fields.image,
                                text: fields.links[index].fields.text,
                            }}
                            className="quick-links-widget"
                        />
                    </React.Fragment>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(QuickLinks),
    definition: {
        group: "Xngage Custom" as any,
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                    } = item.fields;

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                validate: value => (value.length < 5 ? null : "Can't add more than 4 items"),
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        displayName: "link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "linkText",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
